import React from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import DownloadIcon from '../../Images/download-icon.svg';
import SearchIcon from '../../Images/search-icon.svg';
import MapView from './MapView';
import SummaryTabTables from './SummaryTabTables';
import SummaryAlertsModal from './SummaryAlertsModal';
import SummaryGatewayStatusModal from './SummaryGatewayStatusModal';
import {
  SummaryTopFilterDropdowns,
  SummaryAssetTypeDropdown,
  useSummaryDropdownOpenState,
} from './SummaryFilterDropdowns';
import type { useSummaryPage } from './Summary';

export type SummaryPageViewProps = ReturnType<typeof useSummaryPage>;

const SummaryPageView: React.FC<SummaryPageViewProps> = (props) => {
  const dropdownState = useSummaryDropdownOpenState();
  const {
    customers,
    extractedSites,
    extractedPlants,
    assetTypes,
    selectedCustomerId,
    setSelectedCustomerId,
    setSelectedSiteId,
    setSelectedPlantId,
    selectedSiteId,
    selectedPlantId,
    selectedAssetType,
    setSelectedAssetType,
    showAlertsOnly,
    handleAlertsToggleChange,
    activeTab,
    handleTabChange,
    searchText,
    handleSearchChange,
    handleDownloadCSV,
    isDownloadDisabled,
    loading,
    pinnedPageLoading,
    pagedAssetSummary,
    pagedPinnedRows,
    pinnedAssetsMap,
    pinningAssetId,
    sortConfig,
    pinnedSortConfig,
    requestSort,
    handlePinClick,
    handleAssetNameClick,
    handleGatewayStatusClick,
    handleAlertsClick,
    assetsCount,
    pinnedAssetsCount,
    currentPage,
    itemsPerPage,
    totalItems,
    totalPages,
    goToPreviousPage,
    goToNextPage,
    showGatewayStatusModal,
    selectedGatewayStatus,
    closeGatewayStatusModal,
    showAlertsModal,
    selectedAssetAlerts,
    closeAlertsModal,
    handleSiteChange,
    handlePlantChange,
    resetToFirstPage,
  } = props;

  const onSelectCustomer = (customerId: string) => {
    setSelectedCustomerId(customerId);
    setSelectedSiteId('');
    setSelectedPlantId('');
    setSelectedAssetType('');
  };

  const paginationLabel = activeTab === 'assets' ? 'Entries' : 'Pinned Assets';

  return (
    <div className="summary-container home">
      <div className="summary-page-content">
        <div className="summary-filters-container new-summary-filter">
          <SummaryTopFilterDropdowns
            customers={customers}
            extractedSites={extractedSites}
            extractedPlants={extractedPlants}
            selectedCustomerId={selectedCustomerId}
            selectedSiteId={selectedSiteId}
            selectedPlantId={selectedPlantId}
            dropdownState={dropdownState}
            onResetPage={resetToFirstPage}
            onSelectCustomer={onSelectCustomer}
            onSelectSite={handleSiteChange}
            onSelectPlant={handlePlantChange}
          />
        </div>
        <hr />
        <div className="summary-topbar">
          <div className="summary-tabs">
            <span
              className={`summary-tab${activeTab === 'assets' ? ' active' : ''}`}
              onClick={() => handleTabChange('assets')}
              style={{ cursor: 'pointer' }}
            >
              Assets <span className="summary-badge">{assetsCount}</span>
            </span>
            <span
              className={`summary-tab${activeTab === 'pinned' ? ' active' : ''}`}
              onClick={() => handleTabChange('pinned')}
              style={{ cursor: 'pointer' }}
            >
              Pinned <span className="summary-badge pinned">{pinnedAssetsCount}</span>
            </span>
            <span
              className={`summary-tab${activeTab === 'mapview' ? ' active' : ''}`}
              onClick={() => handleTabChange('mapview')}
              style={{ cursor: 'pointer' }}
            >
              Map View
            </span>
          </div>
          <div className="header-right">
            {activeTab !== 'mapview' && (
              <>
                <div className="summary-toggle-group">
                  <label className="switch">
                    <input type="checkbox" checked={showAlertsOnly} onChange={handleAlertsToggleChange} />
                    <span className="slider-add round" />
                  </label>
                  <span className="summary-toggle-text">Show Alerts Only</span>
                </div>
                <SummaryAssetTypeDropdown
                  assetTypes={assetTypes}
                  selectedAssetType={selectedAssetType}
                  dropdownState={dropdownState}
                  onResetPage={resetToFirstPage}
                  onSelectAssetType={setSelectedAssetType}
                />
              </>
            )}
          </div>
        </div>
        <div className="summary-content-body">
          <div className="summary-table-container">
            <div className="summary-searchbar-row">
              <div className="summary-searchbar-group">
                <img src={SearchIcon} alt="Search" className="search-icons" />
                <input
                  type="text"
                  className="~summary-searchbar search-input-plant search-input-summary"
                  placeholder="Search"
                  value={searchText}
                  onChange={handleSearchChange}
                />
              </div>
              {activeTab !== 'mapview' && (
                <button
                  className="summary-download-btn"
                  onClick={handleDownloadCSV}
                  disabled={isDownloadDisabled}
                  style={isDownloadDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                >
                  <img className="download-icon-lg" src={DownloadIcon} alt="Download" />
                  Download
                </button>
              )}
            </div>

            <SummaryTabTables
              activeTab={activeTab}
              loading={loading}
              pinnedPageLoading={pinnedPageLoading}
              pagedAssetSummary={pagedAssetSummary}
              pagedPinnedRows={pagedPinnedRows}
              pinnedAssetsMap={pinnedAssetsMap}
              pinningAssetId={pinningAssetId}
              sortConfig={sortConfig}
              pinnedSortConfig={pinnedSortConfig}
              onRequestSort={requestSort}
              onPinClick={handlePinClick}
              onAssetNameClick={handleAssetNameClick}
              onGatewayStatusClick={handleGatewayStatusClick}
              onAlertsClick={handleAlertsClick}
            />

            {activeTab === 'mapview' && (
              <div className="summary-map-container">
                <MapView
                  selectedCustomerId={selectedCustomerId || undefined}
                  selectedSiteId={selectedSiteId || undefined}
                  selectedPlantId={selectedPlantId || undefined}
                />
              </div>
            )}

            {(activeTab === 'assets' || activeTab === 'pinned') && (
              <div className="summary-pagination">
                <button
                  className="summary-pagination-prev"
                  onClick={goToPreviousPage}
                  disabled={currentPage === 1}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ marginBottom: '-5px' }}
                  >
                    <path
                      d="M15.8333 10L4.16668 10M4.16668 10L10 15.8334M4.16668 10L10 4.16669"
                      stroke="#FCFCFD"
                      strokeWidth="1.67"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {'Previous'}
                </button>
                <span>
                  Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
                  {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} {paginationLabel}
                </span>
                <button
                  className="summary-pagination-next"
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages || totalPages === 0 || !totalPages}
                >
                  {'Next '}
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ marginBottom: '-5px' }}
                  >
                    <path
                      d="M4.16675 9.99996H15.8334M15.8334 9.99996L10.0001 4.16663M15.8334 9.99996L10.0001 15.8333"
                      stroke="#FCFCFD"
                      strokeWidth="1.67"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {showGatewayStatusModal && selectedGatewayStatus && (
        <SummaryGatewayStatusModal state={selectedGatewayStatus} onClose={closeGatewayStatusModal} />
      )}

      {showAlertsModal && selectedAssetAlerts && (
        <SummaryAlertsModal
          assetName={selectedAssetAlerts.assetName}
          alerts={selectedAssetAlerts.alerts}
          onClose={closeAlertsModal}
        />
      )}
    </div>
  );
};

export default SummaryPageView;
